/**
 * WordPress Integration Form
 * 
 * Form for configuring WordPress migration with content, media, and SEO data transfer
 */

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, ExternalLink, Eye, EyeOff, AlertTriangle, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { useCreateWordPressIntegration } from '@/features/integrations/hooks/useIntegrations'
import { WordPressIntegrationFormData } from '@/types/integrations'

const wordpressFormSchema = z.object({
  name: z.string().min(1, 'Integration name is required'),
  site_url: z.string().url('Must be a valid URL').min(1, 'Site URL is required'),
  username: z.string().optional(),
  password: z.string().optional(),
  application_password: z.string().optional(),
  migrate_posts: z.boolean().default(true),
  migrate_pages: z.boolean().default(true),
  migrate_media: z.boolean().default(true),
  migrate_comments: z.boolean().default(false),
  preserve_urls: z.boolean().default(true),
  update_internal_links: z.boolean().default(true),
  migration_batch_size: z.number().min(1).max(100).default(10),
}).refine((data) => {
  return data.application_password || (data.username && data.password)
}, {
  message: 'Either application password or username/password is required',
  path: ['application_password']
})

interface WordPressIntegrationFormProps {
  onSuccess: () => void
  onCancel: () => void
}

const batchSizeOptions = [
  { value: 5, label: '5 items (Slow, very safe)' },
  { value: 10, label: '10 items (Recommended)' },
  { value: 20, label: '20 items (Fast)' },
  { value: 50, label: '50 items (Very fast, higher risk)' },
]

export function WordPressIntegrationForm({ onSuccess, onCancel }: WordPressIntegrationFormProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [showAppPassword, setShowAppPassword] = useState(false)
  const [isTestingConnection, setIsTestingConnection] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [siteInfo, setSiteInfo] = useState<any>(null)
  const [useAppPassword, setUseAppPassword] = useState(true)
  
  const createIntegration = useCreateWordPressIntegration()

  const form = useForm<WordPressIntegrationFormData>({
    resolver: zodResolver(wordpressFormSchema),
    defaultValues: {
      name: 'WordPress Migration',
      migrate_posts: true,
      migrate_pages: true,
      migrate_media: true,
      migrate_comments: false,
      preserve_urls: true,
      update_internal_links: true,
      migration_batch_size: 10,
    },
  })

  const onSubmit = (data: WordPressIntegrationFormData) => {
    createIntegration.mutate(data, {
      onSuccess: () => {
        onSuccess()
      },
    })
  }

  const testConnection = async () => {
    const values = form.getValues()
    if (!values.site_url) {
      form.setError('site_url', { message: 'Site URL is required for testing' })
      return
    }

    setIsTestingConnection(true)
    setConnectionStatus('idle')
    
    try {
      // Simulate API call to test WordPress connection
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Mock successful connection with site info
      setSiteInfo({
        name: 'FixMyBrick WordPress Site',
        version: '6.4.2',
        posts: 150,
        pages: 25,
        media: 340,
        comments: 89,
        plugins: 15,
        theme: 'Custom Theme',
        lastUpdated: '2024-01-15',
      })
      
      setConnectionStatus('success')
    } catch (error) {
      setConnectionStatus('error')
      form.setError('site_url', { message: 'Connection failed. Please check your credentials.' })
    } finally {
      setIsTestingConnection(false)
    }
  }

  const getMigrationEstimate = () => {
    if (!siteInfo) return null

    const items = form.watch()
    let totalItems = 0
    
    if (items.migrate_posts) totalItems += siteInfo.posts
    if (items.migrate_pages) totalItems += siteInfo.pages
    if (items.migrate_media) totalItems += siteInfo.media
    if (items.migrate_comments) totalItems += siteInfo.comments

    const batchSize = items.migration_batch_size
    const estimatedTime = Math.ceil(totalItems / batchSize) * 2 // 2 minutes per batch

    return {
      totalItems,
      estimatedTime: estimatedTime,
      batches: Math.ceil(totalItems / batchSize),
    }
  }

  const estimate = getMigrationEstimate()

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>WordPress Site Configuration</CardTitle>
          <CardDescription>
            Configure connection to your WordPress site for migration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Migration Name</Label>
            <Input
              id="name"
              {...form.register('name')}
              placeholder="WordPress Migration"
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="site_url">WordPress Site URL</Label>
            <Input
              id="site_url"
              {...form.register('site_url')}
              placeholder="https://your-wordpress-site.com"
            />
            {form.formState.errors.site_url && (
              <p className="text-sm text-red-600">{form.formState.errors.site_url.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Authentication */}
      <Card>
        <CardHeader>
          <CardTitle>Authentication</CardTitle>
          <CardDescription>
            Choose your preferred authentication method for accessing the WordPress site
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="auth_app_password"
                checked={useAppPassword}
                onChange={() => setUseAppPassword(true)}
              />
              <Label htmlFor="auth_app_password" className="text-sm">
                Application Password (Recommended)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="auth_user_password"
                checked={!useAppPassword}
                onChange={() => setUseAppPassword(false)}
              />
              <Label htmlFor="auth_user_password" className="text-sm">
                Username & Password
              </Label>
            </div>
          </div>

          {useAppPassword ? (
            <div className="space-y-2">
              <Label htmlFor="application_password">Application Password</Label>
              <div className="relative">
                <Input
                  id="application_password"
                  type={showAppPassword ? 'text' : 'password'}
                  {...form.register('application_password')}
                  placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowAppPassword(!showAppPassword)}
                >
                  {showAppPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Generate an application password in WordPress Users → Profile → Application Passwords
              </p>
              {form.formState.errors.application_password && (
                <p className="text-sm text-red-600">{form.formState.errors.application_password.message}</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  {...form.register('username')}
                  placeholder="WordPress username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    {...form.register('password')}
                    placeholder="WordPress password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={testConnection}
            disabled={isTestingConnection}
            className="w-full"
          >
            {isTestingConnection ? 'Testing Connection...' : 'Test Connection'}
          </Button>

          {connectionStatus === 'success' && siteInfo && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">Connection Successful</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm text-green-700">
                <div>Site: {siteInfo.name}</div>
                <div>WordPress: {siteInfo.version}</div>
                <div>Posts: {siteInfo.posts}</div>
                <div>Pages: {siteInfo.pages}</div>
                <div>Media: {siteInfo.media}</div>
                <div>Comments: {siteInfo.comments}</div>
              </div>
            </div>
          )}

          {connectionStatus === 'error' && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Failed to connect to WordPress site. Please check your URL and credentials.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Migration Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Migration Settings</CardTitle>
          <CardDescription>
            Configure what content to migrate and how to handle the process
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <h4 className="font-medium">Content Types</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="migrate_posts"
                  checked={form.watch('migrate_posts')}
                  onCheckedChange={(checked) => form.setValue('migrate_posts', !!checked)}
                />
                <Label htmlFor="migrate_posts" className="text-sm">
                  Posts {siteInfo && `(${siteInfo.posts})`}
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="migrate_pages"
                  checked={form.watch('migrate_pages')}
                  onCheckedChange={(checked) => form.setValue('migrate_pages', !!checked)}
                />
                <Label htmlFor="migrate_pages" className="text-sm">
                  Pages {siteInfo && `(${siteInfo.pages})`}
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="migrate_media"
                  checked={form.watch('migrate_media')}
                  onCheckedChange={(checked) => form.setValue('migrate_media', !!checked)}
                />
                <Label htmlFor="migrate_media" className="text-sm">
                  Media Files {siteInfo && `(${siteInfo.media})`}
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="migrate_comments"
                  checked={form.watch('migrate_comments')}
                  onCheckedChange={(checked) => form.setValue('migrate_comments', !!checked)}
                />
                <Label htmlFor="migrate_comments" className="text-sm">
                  Comments {siteInfo && `(${siteInfo.comments})`}
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium">URL & SEO Settings</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="preserve_urls"
                  checked={form.watch('preserve_urls')}
                  onCheckedChange={(checked) => form.setValue('preserve_urls', !!checked)}
                />
                <Label htmlFor="preserve_urls" className="text-sm">
                  Preserve original URLs (create redirects)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="update_internal_links"
                  checked={form.watch('update_internal_links')}
                  onCheckedChange={(checked) => form.setValue('update_internal_links', !!checked)}
                />
                <Label htmlFor="update_internal_links" className="text-sm">
                  Update internal links to new URLs
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="migration_batch_size">Batch Size</Label>
            <Select
              value={form.watch('migration_batch_size').toString()}
              onValueChange={(value) => form.setValue('migration_batch_size', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select batch size" />
              </SelectTrigger>
              <SelectContent>
                {batchSizeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Number of items to migrate in each batch. Smaller batches are safer but slower.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Migration Estimate */}
      {estimate && connectionStatus === 'success' && (
        <Card>
          <CardHeader>
            <CardTitle>Migration Estimate</CardTitle>
            <CardDescription>
              Estimated time and resources for this migration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{estimate.totalItems}</div>
                <div className="text-sm text-muted-foreground">Total Items</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{estimate.batches}</div>
                <div className="text-sm text-muted-foreground">Batches</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {Math.floor(estimate.estimatedTime / 60)}h {estimate.estimatedTime % 60}m
                </div>
                <div className="text-sm text-muted-foreground">Est. Time</div>
              </div>
            </div>
            
            <Alert>
              <AlertDescription>
                Migration will run in the background. You can monitor progress and pause/resume as needed.
                The site will remain accessible during migration.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Form Actions */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          type="submit" 
          disabled={createIntegration.isPending || connectionStatus !== 'success'}
        >
          {createIntegration.isPending ? 'Setting Up Migration...' : 'Start Migration Setup'}
        </Button>
      </div>
    </form>
  )
}